// apps/widget/src/state.tsx
//
// One state machine for the whole flow. A widget with router state, form state
// and scan state in three places drifts — this keeps "where are we and what do
// we know" in a single object.

import {
  useEffect, createContext, useCallback, useContext, useMemo, useReducer, type ReactNode } from 'react';
import type { Report, ScanSession, WidgetConfig } from './lib/api';

export type Step =
  | 'welcome'
  | 'consent'
  | 'intake'
  | 'prepare'
  | 'capture'
  | 'qr'
  | 'analysing'
  | 'report'
  | 'recommendations'
  | 'routine'
  | 'lead'
  | 'booking'
  | 'error';

export interface Intake {
  declaredAge?: number;
  /** yyyy-mm-dd, when they gave their date of birth rather than an age. */
  dob?: string;
  pregnant?: boolean;
  onRetinoids?: boolean;
  goal?: string;
}

export interface ScanState {
  step: Step;
  config: WidgetConfig;
  consented: boolean;
  marketingOptIn: boolean;
  /** The separate, optional "keep my photo" agreement. */
  retainPhoto: boolean;
  /**
   * Clinic staff viewing this report from their dashboard. The report is the
   * patient's, unchanged; only the patient-facing actions (close, email me
   * this, book) are hidden.
   */
  staffView: boolean;
  intake: Intake;
  session: ScanSession | null;
  scanId: string | null;
  report: Report | null;
  /** Shown on the desktop the instant the phone captures. */
  thumbnailUrl: string | null;
  progressLabel: string;
  error: { code: string; message: string; fixes?: string[] } | null;
  /**
   * The photo as it was captured, plus where the face was in it, for the
   * report's annotated view. Held in this browser tab only — an object URL that
   * is revoked when it is replaced or the widget closes. The uploaded copy is
   * deleted server-side after analysis, exactly as the consent screen says —
   * unless the visitor ticked "keep my photo", in which case the clinic keeps
   * it for the period they were shown.
   */
  capture: CapturedFace | null;
}

export interface CapturedFace {
  photoUrl: string;
  width: number;
  height: number;
  /** MediaPipe landmarks, normalised 0–1 against the captured frame. */
  landmarks: { x: number; y: number }[];
}

type Action =
  | { type: 'go'; step: Step }
  | { type: 'consent'; marketingOptIn: boolean; retainPhoto: boolean }
  | { type: 'staffReport'; report: Report; capture: CapturedFace | null }
  | { type: 'intake'; intake: Intake }
  | { type: 'session'; session: ScanSession }
  | { type: 'thumbnail'; url: string }
  | { type: 'captured'; capture: CapturedFace }
  | { type: 'progress'; label: string }
  | { type: 'scanning'; scanId: string }
  | { type: 'report'; report: Report }
  | { type: 'error'; error: ScanState['error'] }
  | { type: 'restart' }
  // The dashboard's live preview restyling an open frame; nothing else changes.
  | { type: 'config'; config: WidgetConfig };

function reducer(state: ScanState, action: Action): ScanState {
  switch (action.type) {
    case 'go':
      return { ...state, step: action.step, error: null };
    case 'config':
      return { ...state, config: action.config };
    case 'consent':
      // Consent is recorded before the camera is ever touched. The permission
      // prompt must not fire before the visitor has read what is captured.
      return {
        ...state,
        consented: true,
        marketingOptIn: action.marketingOptIn,
        retainPhoto: action.retainPhoto,
        step: 'intake',
      };
    case 'staffReport':
      return {
        ...state,
        report: action.report,
        scanId: action.report.scanId,
        capture: action.capture,
        staffView: true,
        step: 'report',
        error: null,
      };
    case 'intake':
      // The photo tips come before the camera; a retake (restart) skips them.
      return { ...state, intake: action.intake, step: 'prepare' };
    case 'session':
      return { ...state, session: action.session };
    case 'thumbnail':
      return { ...state, thumbnailUrl: action.url };
    case 'captured':
      return { ...state, capture: action.capture };
    case 'progress':
      return { ...state, progressLabel: action.label };
    case 'scanning':
      return { ...state, scanId: action.scanId, step: 'analysing' };
    case 'report':
      // scanId too: a report opened from a link never went through 'scanning',
      // and without it the treatments tab had nothing to load.
      return { ...state, report: action.report, scanId: action.report.scanId, step: 'report', error: null };
    case 'error':
      return { ...state, error: action.error, step: 'error' };
    case 'restart':
      return {
        ...state,
        step: 'capture',
        session: null,
        scanId: null,
        report: null,
        thumbnailUrl: null,
        capture: null,
        error: null,
      };
  }
}

const ScanContext = createContext<{
  state: ScanState;
  dispatch: (action: Action) => void;
  go: (step: Step) => void;
} | null>(null);

export function ScanProvider({ config, children }: { config: WidgetConfig; children: ReactNode }) {
  const [state, dispatch] = useReducer(reducer, {
    step: 'welcome',
    config,
    consented: false,
    marketingOptIn: false,
    retainPhoto: false,
    staffView: false,
    intake: {},
    session: null,
    scanId: null,
    report: null,
    thumbnailUrl: null,
    progressLabel: '',
    error: null,
    capture: null,
  });

  // Release the in-memory photo whenever it is replaced or the widget goes
  // away, so a closed widget leaves nothing of the visitor's face behind.
  useEffect(() => {
    const url = state.capture?.photoUrl;
    return () => {
      // Only object URLs are ours to revoke; a kept photo arrives as a signed link.
      if (url?.startsWith('blob:')) URL.revokeObjectURL(url);
    };
  }, [state.capture?.photoUrl]);

  // Only ever a new object in the dashboard preview; a patient's config is
  // loaded once and never replaced.
  useEffect(() => {
    if (config !== state.config) dispatch({ type: 'config', config });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config]);

  const go = useCallback((step: Step) => dispatch({ type: 'go', step }), []);
  const value = useMemo(() => ({ state, dispatch, go }), [state, go]);

  return <ScanContext.Provider value={value}>{children}</ScanContext.Provider>;
}

export function useScan() {
  const context = useContext(ScanContext);
  if (!context) throw new Error('useScan must be used inside ScanProvider');
  return context;
}
